import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { InstallButton } from '@/context/InstallContext';
import { formatDate } from '@/utils/dateFormat';

export default function MarketerDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [customers, setCustomers] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [registeredVehicles, setRegisteredVehicles] = useState([]);
  const [orgServices, setOrgServices] = useState({ organizations: [], summary: {} });
  const [referralCode, setReferralCode] = useState('');
  const [stats, setStats] = useState({ total_earned: 0, total_services: 0 });
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState(null);
  
  // Dialog states
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  
  // Form states
  const [customerForm, setCustomerForm] = useState({
    name: '', email: '', phone: '', address: '', password: ''
  });
  const [orgForm, setOrgForm] = useState({
    name: '', organization_type: '', email: '', phone: '', address: '', password: '',
    company_size: '', industry_type: ''
  });
  const [vehicleForm, setVehicleForm] = useState({
    user_id: '', make: '', model: '', year: '', license_plate: '',
    vin: '', current_mileage: '', last_service_date: '', last_oil_type: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const OIL_TYPES = ['Synthetic Oil', 'Semi-Synthetic Oil', 'Mineral Oil', 'High-Mileage Mineral Oil'];
  const ORG_TYPES = ['government', 'private', 'ngo'];
  const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];
  const INDUSTRY_TYPES = ['logistics', 'construction', 'healthcare', 'government', 'education', 'retail', 'other'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, commissionsRes, orgsRes, usersRes, vehiclesRes, orgServicesRes, referralRes] = await Promise.all([
        axios.get(`${API}/marketer/customers`),
        axios.get(`${API}/marketer/commissions`),
        axios.get(`${API}/marketer/organizations`),
        axios.get(`${API}/marketer/users`),
        axios.get(`${API}/marketer/registered-vehicles`),
        axios.get(`${API}/marketer/organization-services`),
        axios.get(`${API}/marketer/referral-code`)
      ]);
      setCustomers(customersRes.data);
      setCommissions(commissionsRes.data.commissions);
      setStats({
        total_earned: commissionsRes.data.total_earned,
        total_services: commissionsRes.data.total_services,
        total_customer_earned: commissionsRes.data.total_customer_earned || 0,
        total_org_earned: commissionsRes.data.total_org_earned || 0,
        total_customer_services: commissionsRes.data.total_customer_services || 0,
        total_org_services: commissionsRes.data.total_org_services || 0
      });
      setOrganizations(orgsRes.data);
      setAllUsers(usersRes.data);
      setRegisteredVehicles(vehiclesRes.data);
      setOrgServices(orgServicesRes.data);
      setReferralCode(referralRes.data.referral_code);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const registerCustomer = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/marketer/register-customer`, customerForm);
      toast.success('Customer registered successfully!');
      setCustomerForm({ name: '', email: '', phone: '', address: '', password: '' });
      setCustomerDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const registerOrganization = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/marketer/register-organization`, orgForm);
      toast.success('Organization registered successfully!');
      setOrgForm({ name: '', organization_type: '', email: '', phone: '', address: '', password: '', company_size: '', industry_type: '' });
      setOrgDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const registerVehicle = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...vehicleForm,
        year: parseInt(vehicleForm.year),
        current_mileage: vehicleForm.current_mileage ? parseInt(vehicleForm.current_mileage) : null
      };
      await axios.post(`${API}/marketer/register-vehicle`, payload);
      toast.success('Vehicle registered successfully!');
      setVehicleForm({ user_id: '', make: '', model: '', year: '', license_plate: '', vin: '', current_mileage: '', last_service_date: '', last_oil_type: '' });
      setVehicleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl font-heading text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="marketer-dashboard">
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <span className="text-xl font-heading font-bold text-foreground block">EXCEL ERA</span>
                <span className="text-xs text-green-600 font-bold uppercase tracking-wide">Marketer Portal</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <InstallButton variant="default" size="sm" />
              <span className="text-sm font-medium text-muted-foreground hidden sm:inline">Hello, {user?.name}</span>
              <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="font-bold uppercase text-sm">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-green-600 text-white p-6 rounded-sm shadow-hard mb-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-heading font-bold mb-1">Marketer Dashboard</h1>
              <p className="text-sm opacity-90">Register customers, organizations, and vehicles. Earn ₦500 commission per service</p>
            </div>
            {referralCode && (
              <div className="bg-white/20 backdrop-blur rounded-sm p-4 text-center" data-testid="referral-code-card">
                <div className="text-xs uppercase tracking-wide opacity-90 mb-1">Your Referral Code</div>
                <div className="text-3xl font-mono font-black tracking-widest">{referralCode}</div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-2 text-white hover:bg-white/20 text-xs"
                  onClick={() => {
                    navigator.clipboard.writeText(referralCode);
                    toast.success('Referral code copied!');
                  }}
                  data-testid="copy-referral-btn"
                >
                  📋 Copy Code
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-green-600 mb-2">{customers.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">My Customers</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-blue-600 mb-2">{organizations.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Organizations</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-orange-600 mb-2">{orgServices.summary?.total_services_completed || 0}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Org Services</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-primary mb-2">{stats.total_services}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Total Services</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-secondary mb-2 font-mono">₦{stats.total_earned.toLocaleString()}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Total Earned</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide px-6 py-3 h-auto rounded-sm" data-testid="register-customer-btn">
                + Register Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading font-bold text-foreground">Register New Customer</DialogTitle>
              </DialogHeader>
              <form onSubmit={registerCustomer} className="space-y-4">
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Full Name</Label>
                  <Input required data-testid="customer-name-input" className="input-field mt-2"
                    value={customerForm.name} onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })} />
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Email</Label>
                  <Input type="email" required data-testid="customer-email-input" className="input-field mt-2"
                    value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} />
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Phone</Label>
                  <Input type="tel" required data-testid="customer-phone-input" className="input-field mt-2" placeholder="+234 XXX XXX XXXX"
                    value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Service Address</Label>
                  <Input required data-testid="customer-address-input" className="input-field mt-2" placeholder="Customer location"
                    value={customerForm.address} onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })} />
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Initial Password</Label>
                  <Input type="password" required data-testid="customer-password-input" className="input-field mt-2"
                    value={customerForm.password} onChange={(e) => setCustomerForm({ ...customerForm, password: e.target.value })} />
                </div>
                <Button type="submit" disabled={isSubmitting} data-testid="customer-submit-btn"
                  className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide w-full h-12 rounded-sm">
                  {isSubmitting ? 'Registering...' : 'Register Customer'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wide px-6 py-3 h-auto rounded-sm" data-testid="register-org-btn">
                + Register Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading font-bold text-foreground">Register Organization</DialogTitle>
                <p className="text-sm text-muted-foreground">You will be set as the contact person</p>
              </DialogHeader>
              <form onSubmit={registerOrganization} className="space-y-4">
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Organization Name</Label>
                  <Input required data-testid="org-name-input" className="input-field mt-2"
                    value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })} />
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Organization Type</Label>
                  <Select value={orgForm.organization_type} onValueChange={(v) => setOrgForm({ ...orgForm, organization_type: v })}>
                    <SelectTrigger className="input-field mt-2" data-testid="org-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ORG_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Company Size</Label>
                    <Select value={orgForm.company_size} onValueChange={(v) => setOrgForm({ ...orgForm, company_size: v })}>
                      <SelectTrigger className="input-field mt-2" data-testid="org-size-select">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_SIZES.map(s => <SelectItem key={s} value={s}>{s} employees</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Industry</Label>
                    <Select value={orgForm.industry_type} onValueChange={(v) => setOrgForm({ ...orgForm, industry_type: v })}>
                      <SelectTrigger className="input-field mt-2" data-testid="org-industry-select">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRY_TYPES.map(i => <SelectItem key={i} value={i}>{i.charAt(0).toUpperCase() + i.slice(1)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Email</Label>
                  <Input type="email" required data-testid="org-email-input" className="input-field mt-2"
                    value={orgForm.email} onChange={(e) => setOrgForm({ ...orgForm, email: e.target.value })} />
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Phone</Label>
                  <Input type="tel" required data-testid="org-phone-input" className="input-field mt-2"
                    value={orgForm.phone} onChange={(e) => setOrgForm({ ...orgForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Address</Label>
                  <Input required data-testid="org-address-input" className="input-field mt-2"
                    value={orgForm.address} onChange={(e) => setOrgForm({ ...orgForm, address: e.target.value })} />
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Login Password</Label>
                  <Input type="password" required data-testid="org-password-input" className="input-field mt-2"
                    value={orgForm.password} onChange={(e) => setOrgForm({ ...orgForm, password: e.target.value })} />
                </div>
                <Button type="submit" disabled={isSubmitting || !orgForm.organization_type} data-testid="org-submit-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wide w-full h-12 rounded-sm">
                  {isSubmitting ? 'Registering...' : 'Register Organization'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white font-bold uppercase tracking-wide px-6 py-3 h-auto rounded-sm" data-testid="register-vehicle-btn">
                + Register Vehicle for User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading font-bold text-foreground">Register Vehicle for User</DialogTitle>
              </DialogHeader>
              <form onSubmit={registerVehicle} className="space-y-4">
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Select User</Label>
                  <Select value={vehicleForm.user_id} onValueChange={(v) => setVehicleForm({ ...vehicleForm, user_id: v })}>
                    <SelectTrigger className="input-field mt-2" data-testid="vehicle-user-select">
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent>
                      {allUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.phone})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Make</Label>
                    <Input required data-testid="vehicle-make-input" className="input-field mt-2" placeholder="e.g., Toyota"
                      value={vehicleForm.make} onChange={(e) => setVehicleForm({ ...vehicleForm, make: e.target.value })} />
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Model</Label>
                    <Input required data-testid="vehicle-model-input" className="input-field mt-2" placeholder="e.g., Camry"
                      value={vehicleForm.model} onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Year</Label>
                    <Input type="number" required data-testid="vehicle-year-input" className="input-field mt-2" placeholder="e.g., 2020"
                      value={vehicleForm.year} onChange={(e) => setVehicleForm({ ...vehicleForm, year: e.target.value })} />
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">License Plate</Label>
                    <Input required data-testid="vehicle-plate-input" className="input-field mt-2" placeholder="e.g., ABC-123XY"
                      value={vehicleForm.license_plate} onChange={(e) => setVehicleForm({ ...vehicleForm, license_plate: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">VIN (Optional)</Label>
                  <Input data-testid="vehicle-vin-input" className="input-field mt-2"
                    value={vehicleForm.vin} onChange={(e) => setVehicleForm({ ...vehicleForm, vin: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Current Mileage (km)</Label>
                    <Input type="number" data-testid="vehicle-mileage-input" className="input-field mt-2"
                      value={vehicleForm.current_mileage} onChange={(e) => setVehicleForm({ ...vehicleForm, current_mileage: e.target.value })} />
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm tracking-wide">Last Service Date</Label>
                    <Input type="date" data-testid="vehicle-service-date-input" className="input-field mt-2"
                      value={vehicleForm.last_service_date} onChange={(e) => setVehicleForm({ ...vehicleForm, last_service_date: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="font-bold uppercase text-sm tracking-wide">Last Oil Type</Label>
                  <Select value={vehicleForm.last_oil_type} onValueChange={(v) => setVehicleForm({ ...vehicleForm, last_oil_type: v })}>
                    <SelectTrigger className="input-field mt-2" data-testid="vehicle-oil-select">
                      <SelectValue placeholder="Select oil type" />
                    </SelectTrigger>
                    <SelectContent>
                      {OIL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={isSubmitting || !vehicleForm.user_id} data-testid="vehicle-submit-btn"
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold uppercase tracking-wide w-full h-12 rounded-sm">
                  {isSubmitting ? 'Registering...' : 'Register Vehicle'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs for different sections */}
        <Tabs defaultValue="customers" className="space-y-6">
          <TabsList className="bg-white border-2 border-border p-1 h-auto flex-wrap">
            <TabsTrigger value="customers" className="font-bold uppercase text-sm px-6 py-3 data-[state=active]:bg-green-600 data-[state=active]:text-white">
              Customers ({customers.length})
            </TabsTrigger>
            <TabsTrigger value="organizations" className="font-bold uppercase text-sm px-6 py-3 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Organizations ({organizations.length})
            </TabsTrigger>
            <TabsTrigger value="org-services" className="font-bold uppercase text-sm px-6 py-3 data-[state=active]:bg-orange-600 data-[state=active]:text-white" data-testid="org-services-tab">
              Org Services ({orgServices.summary?.total_services_completed || 0})
            </TabsTrigger>
            <TabsTrigger value="vehicles" className="font-bold uppercase text-sm px-6 py-3 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              Vehicles ({registeredVehicles.length})
            </TabsTrigger>
            <TabsTrigger value="commissions" className="font-bold uppercase text-sm px-6 py-3 data-[state=active]:bg-secondary data-[state=active]:text-white">
              Commissions
            </TabsTrigger>
          </TabsList>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-6">My Registered Customers</h2>
            {customers.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground mb-4">No customers registered yet</p>
                <Button onClick={() => setCustomerDialogOpen(true)} className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide px-8 py-3 h-auto rounded-sm">
                  Register First Customer
                </Button>
              </div>
            ) : (
              <div className="bg-white border-2 border-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="customers-table">
                    <thead className="bg-green-600 text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Name</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Email</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Phone</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Registered</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {customers.map(customer => (
                        <tr key={customer.id} className="hover:bg-accent transition-colors">
                          <td className="px-6 py-4 font-bold text-foreground">{customer.name}</td>
                          <td className="px-6 py-4 font-mono text-sm text-foreground">{customer.email}</td>
                          <td className="px-6 py-4 font-mono text-sm text-foreground">{customer.phone}</td>
                          <td className="px-6 py-4 text-sm text-muted-foreground">
                            {new Date(customer.created_at).toLocaleDateString('en-GB')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Organizations Tab */}
          <TabsContent value="organizations">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-6">My Registered Organizations</h2>
            {organizations.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground mb-4">No organizations registered yet</p>
                <Button onClick={() => setOrgDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wide px-8 py-3 h-auto rounded-sm">
                  Register First Organization
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {organizations.map(org => (
                  <div key={org.id} className="bg-white border-2 border-border p-6 rounded-sm" data-testid="org-card">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-heading font-bold text-foreground">{org.name}</h3>
                        <span className="text-xs font-bold px-3 py-1 rounded-sm uppercase bg-blue-100 text-blue-800">
                          {org.organization_type}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-heading font-black text-blue-600">{org.vehicle_count || 0}</div>
                        <div className="text-xs text-muted-foreground">vehicles</div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-bold text-muted-foreground">Email:</span> {org.email}</p>
                      <p><span className="font-bold text-muted-foreground">Phone:</span> {org.phone}</p>
                      {org.company_size && <p><span className="font-bold text-muted-foreground">Size:</span> {org.company_size} employees</p>}
                      {org.industry_type && <p><span className="font-bold text-muted-foreground">Industry:</span> {org.industry_type}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-6">Vehicles Registered for Users</h2>
            {registeredVehicles.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground mb-4">No vehicles registered yet</p>
                <Button onClick={() => setVehicleDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white font-bold uppercase tracking-wide px-8 py-3 h-auto rounded-sm">
                  Register First Vehicle
                </Button>
              </div>
            ) : (
              <div className="bg-white border-2 border-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="vehicles-table">
                    <thead className="bg-purple-600 text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Vehicle</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Owner</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">License Plate</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Mileage</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Next Service</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {registeredVehicles.map(vehicle => (
                        <tr key={vehicle.id} className="hover:bg-accent transition-colors">
                          <td className="px-6 py-4 font-bold text-foreground">
                            {vehicle.make} {vehicle.model} ({vehicle.year})
                          </td>
                          <td className="px-6 py-4 text-sm text-foreground">
                            <div>{vehicle.user_name || 'N/A'}</div>
                            <div className="text-xs text-muted-foreground">{vehicle.user_phone}</div>
                          </td>
                          <td className="px-6 py-4 font-mono text-sm text-foreground">{vehicle.license_plate}</td>
                          <td className="px-6 py-4 text-sm text-foreground">
                            {vehicle.current_mileage ? `${vehicle.current_mileage.toLocaleString()} km` : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-foreground">
                            {vehicle.next_service_date ? formatDate(vehicle.next_service_date) : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Org Services Tab */}
          <TabsContent value="org-services">
            <div className="mb-6">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-4">Organization Service Tracking</h2>
              {/* Summary Cards */}
              <div className="grid md:grid-cols-5 gap-4 mb-6">
                <div className="bg-orange-50 border-2 border-orange-200 p-4 rounded-sm">
                  <div className="text-3xl font-heading font-black text-orange-600">{orgServices.summary?.total_organizations || 0}</div>
                  <div className="text-xs font-bold uppercase tracking-wide text-orange-700">Organizations</div>
                </div>
                <div className="bg-orange-50 border-2 border-orange-200 p-4 rounded-sm">
                  <div className="text-3xl font-heading font-black text-orange-600">{orgServices.summary?.total_vehicles_across_orgs || 0}</div>
                  <div className="text-xs font-bold uppercase tracking-wide text-orange-700">Fleet Vehicles</div>
                </div>
                <div className="bg-orange-50 border-2 border-orange-200 p-4 rounded-sm">
                  <div className="text-3xl font-heading font-black text-orange-600">{orgServices.summary?.total_vehicles_serviced || 0}</div>
                  <div className="text-xs font-bold uppercase tracking-wide text-orange-700">Vehicles Serviced</div>
                </div>
                <div className="bg-orange-50 border-2 border-orange-200 p-4 rounded-sm">
                  <div className="text-3xl font-heading font-black text-orange-600">{orgServices.summary?.total_services_completed || 0}</div>
                  <div className="text-xs font-bold uppercase tracking-wide text-orange-700">Total Services</div>
                </div>
                <div className="bg-green-50 border-2 border-green-200 p-4 rounded-sm">
                  <div className="text-3xl font-heading font-black text-green-600 font-mono">₦{(orgServices.summary?.total_commission_earned || 0).toLocaleString()}</div>
                  <div className="text-xs font-bold uppercase tracking-wide text-green-700">Org Commission</div>
                </div>
              </div>
            </div>

            {orgServices.organizations?.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground mb-4">No organizations registered yet</p>
                <Button onClick={() => setOrgDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wide px-8 py-3 h-auto rounded-sm">
                  Register First Organization
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {orgServices.organizations?.map(org => (
                  <div key={org.organization_id} className="bg-white border-2 border-border rounded-sm overflow-hidden" data-testid="org-service-card">
                    {/* Organization Header - Clickable */}
                    <div 
                      className="p-6 cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => setExpandedOrg(expandedOrg === org.organization_id ? null : org.organization_id)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                          <div>
                            <h3 className="text-xl font-heading font-bold text-foreground">{org.organization_name}</h3>
                            <span className="text-xs font-bold px-2 py-1 rounded-sm uppercase bg-blue-100 text-blue-800">
                              {org.organization_type}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <div className="text-2xl font-heading font-black text-blue-600">{org.total_vehicles}</div>
                            <div className="text-xs text-muted-foreground">Vehicles</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-heading font-black text-green-600">{org.vehicles_serviced}</div>
                            <div className="text-xs text-muted-foreground">Serviced</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-heading font-black text-orange-600">{org.total_services}</div>
                            <div className="text-xs text-muted-foreground">Services</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-heading font-black text-green-600 font-mono">₦{(org.commission_earned || 0).toLocaleString()}</div>
                            <div className="text-xs text-muted-foreground">Earned</div>
                          </div>
                          <div className="text-2xl text-muted-foreground">
                            {expandedOrg === org.organization_id ? '▲' : '▼'}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Service History */}
                    {expandedOrg === org.organization_id && (
                      <div className="border-t-2 border-border bg-accent/30">
                        {org.service_history?.length === 0 ? (
                          <div className="p-6 text-center text-muted-foreground">
                            <p className="font-medium">No services completed yet for this organization</p>
                            <p className="text-sm mt-1">Services will appear here once fleet vehicles are serviced</p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-orange-600 text-white">
                                <tr>
                                  <th className="px-4 py-3 text-left font-bold uppercase text-xs tracking-wide">Vehicle</th>
                                  <th className="px-4 py-3 text-left font-bold uppercase text-xs tracking-wide">License Plate</th>
                                  <th className="px-4 py-3 text-left font-bold uppercase text-xs tracking-wide">Last Service</th>
                                  <th className="px-4 py-3 text-left font-bold uppercase text-xs tracking-wide">Service Type</th>
                                  <th className="px-4 py-3 text-left font-bold uppercase text-xs tracking-wide">Technician</th>
                                  <th className="px-4 py-3 text-left font-bold uppercase text-xs tracking-wide">Total Services</th>
                                  <th className="px-4 py-3 text-left font-bold uppercase text-xs tracking-wide">Next Service</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border">
                                {org.service_history?.map((service, idx) => (
                                  <tr key={idx} className="hover:bg-accent transition-colors">
                                    <td className="px-4 py-3 font-medium text-foreground">{service.vehicle_info}</td>
                                    <td className="px-4 py-3 font-mono text-sm">{service.license_plate}</td>
                                    <td className="px-4 py-3 text-sm">{formatDate(service.last_service_date)}</td>
                                    <td className="px-4 py-3 text-sm">{service.last_service_type}</td>
                                    <td className="px-4 py-3 text-sm">{service.technician_name || 'N/A'}</td>
                                    <td className="px-4 py-3">
                                      <span className="font-bold text-orange-600">{service.total_services}</span>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      {service.next_service_date ? formatDate(service.next_service_date) : 'N/A'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-6">Commission History</h2>
            {commissions.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground">No commissions earned yet</p>
                <p className="text-sm text-muted-foreground mt-2">Earn ₦500 each time your customers get serviced</p>
              </div>
            ) : (
              <div className="space-y-4">
                {commissions.map((commission, index) => {
                  const customer = customers.find(c => c.id === commission.customer_id);
                  return (
                    <div key={index} className="bg-white border-2 border-border p-6 rounded-sm shadow-sm" data-testid="commission-card">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-heading font-bold text-foreground">{customer?.name || 'Customer'}</h3>
                          <p className="text-sm text-muted-foreground">Service Date: {formatDate(commission.service_date)}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-heading font-black text-green-600 font-mono">₦{commission.amount.toLocaleString()}</div>
                          <span className="text-xs font-bold px-3 py-1 rounded-sm uppercase bg-green-100 text-green-800">
                            {commission.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
